import { useCallback, useEffect, useState } from 'react'

import Action from 'ageas-react-component-action'
import Date from 'ageas-react-component-datepicker'
import Dropdown from 'ageas-react-component-dropdownlist'
import Message from 'ageas-react-component-message'
import TextField from 'ageas-react-component-textfield'
import moment from 'moment'

import { capitalize, map } from 'lodash'
import { useAnimalStore } from '../../Stores/animalStore'
import { useSimulatorStore } from '../../Stores/simulatorStore'
import { useAnimalValidateStore } from '../../Stores/useAnimalValidateStore'
import { useFillDataForSimulationStore } from '../../Stores/useFillDataForSimulationStore'
import { useGenerateProposalDocumentsStore } from '../../Stores/useGenerateProposalDocumentsStore'
import { useRunPetisSimulationStore } from '../../Stores/useRunPetisSimulationStore'
import { AnimalsSecured } from '../../Types/AnimalStore'
import { SPECIES } from '../../constants/species'
import { t } from '../../i18n'
import { validateObjectValues } from '../../utils/validates'
import { ActionTab, FormContainer } from './styles'

type Props = {
  disabled?: boolean
  animal: AnimalsSecured
  setIsValid: (value: Record<string, any>) => void
  onNextTab?: () => void
}

const TODAY = moment()
const TODAY_FORMATTED = TODAY.format('DD-MM-YYYY')
const TEN_YEARS_AGO = TODAY.subtract(10, 'years').format('DD-MM-YYYY')

export const AnimalInputData = ({
  animal,
  setIsValid,
  disabled,
  onNextTab,
}: Props) => {
  const [currentSpecies, setCurrentSpecies] = useState(animal.species as string)
  const [hasUpdate, setHasUpdate] = useState(true)
  const [disabledNextTabButton, setDisabledNextTabButton] = useState(true)
  const [dateErrorMessage, setDateErrorMessage] = useState('')
  const [birthDay, setBirthDay] = useState(
    moment(animal.birthDate).format('DD-MM-YYYY'),
  )
  const { setEnabledRunPetisRequest } = useRunPetisSimulationStore()
  const { setBreeds, breeds } = useSimulatorStore()
  const { fillDataForSimulationRequest } = useFillDataForSimulationStore()
  const { updateAnimal } = useAnimalStore()
  const { oldSpecies, setOldSpecies } = useAnimalValidateStore()
  const { isGenerateProposalDocuments } = useGenerateProposalDocumentsStore()

  const formatterSpeciesOptions =
    fillDataForSimulationRequest?.petDetailsResponse?.petSpeciesList?.map(
      (specie) => ({
        caption: specie.shortDescription,
        value: specie.code,
      }),
    )

  const formatterBreedOptionsBySpecies = (value = currentSpecies) => {
    if (value === undefined || value.length === 0) return

    const currentSpecie = formatterSpeciesOptions?.find(
      (item) => item.caption === value,
    )?.value

    return currentSpecie === SPECIES.dog
      ? fillDataForSimulationRequest?.petDetailsResponse?.dogBreedsList?.map(
          (breed) => ({
            caption: breed.shortDescription,
            value: breed.code,
            isDangerousAnimal: breed.isDangerousAnimal || false,
          }),
        )
      : fillDataForSimulationRequest?.petDetailsResponse?.catBreedsList?.map(
          (breed) => ({
            caption: breed.shortDescription,
            value: breed.code,
            isDangerousAnimal: breed.isDangerousAnimal || false,
          }),
        )
  }

  const handleEditSafeAnimal = useCallback(
    (field: string, value: number | string | Date) => {
      if (field === 'species') {
        if (oldSpecies?.[`species-${animal.id}`] !== value) {
          setCurrentSpecies(value as string)
          updateAnimal({ breed: '' }, animal.id)
          setBreeds(formatterBreedOptionsBySpecies(value as string))

          const breedElement: any = document?.querySelector(
            `#breed-${animal.id}dropText`,
          )

          breedElement!.value = ''

          setOldSpecies({ [`species-${animal.id}`]: value as string })
        }
      }

      updateAnimal(
        {
          [field]:
            field === 'birthDate' && value !== ''
              ? moment(value, 'DD-MM-YYYY').format()
              : value,
        },
        animal.id,
      )
      setHasUpdate(true)
      setEnabledRunPetisRequest(true)
    },
    [animal.id, updateAnimal, oldSpecies],
  )

  const checkFormValidator = useCallback(() => {
    const animalBirthdate = moment(animal.birthDate, 'YYYY-MM-DD')

    const formAnimal = {
        birthDate: animalBirthdate.isAfter(moment(), 'day') || animalBirthdate.isBefore(moment(TEN_YEARS_AGO, 'DD-MM-YYYY'))
        ? ''
        : animal.birthDate,
      species: animal.species,
      breed: animal.breed,
    }

    const validate = validateObjectValues(formAnimal)
    setIsValid({ [`formAnimal${animal.id}`]: validate })
    setDisabledNextTabButton(!validate)
  }, [animal, setIsValid])

  useEffect(() => {
    if (hasUpdate) {
      checkFormValidator()
      setHasUpdate(false)
    }
  }, [animal.id, checkFormValidator, hasUpdate, setIsValid])

  return (
    <>
      <FormContainer>
        <div className="message">
          <Message type="warning" text={t('pages.step1.mandatory')} />
        </div>
        <div className="row">
          <div className="col">
            <TextField
              readOnly={isGenerateProposalDocuments || disabled}
              id={`animalName-${animal.id}}-${animal.name}`}
              key={`animalName-${animal.id}-${animal.name}`}
              name={`animalName-${animal.id}}-${animal.name}`}
              label={t('pages.step1.animalInputData.name')}
              optionalLabel
              value={animal.name}
              onValueChanged={(id: any, value: any) => {
                handleEditSafeAnimal(
                  'name',
                  map(value.split(' '), capitalize).join(' '),
                )
              }}
              colorTheme="blue"
              maxLength={50}
              version="2"
            />
          </div>
          <div className="col">
            <Date
              readOnly={isGenerateProposalDocuments || disabled}
              Picker
              required
              id={`birthDate`}
              name={`birthDate-${animal.id}`}
              label={t('pages.step1.animalInputData.birthdate')}
              futureDate={TODAY_FORMATTED}
              pastDate={TEN_YEARS_AGO}
              format="d-m-Y"
              message={dateErrorMessage}
              colorTheme="blue"
              value={birthDay === 'Invalid date' ? '' : birthDay}
              onValueChanged={(key: any, value: string) => {
                if (moment(value, 'DD-MM-YYYY', true).isValid()) {
                  setBirthDay(value)
                  handleEditSafeAnimal(key, value)
                  setDateErrorMessage('')
                } else {
                  setDateErrorMessage(
                    t('pages.step1.animalInputData.birthdateError'),
                  )
                }
              }}
              version="2"
            />
          </div>
        </div>
        <div className="row">
          <Dropdown
            readOnly={isGenerateProposalDocuments || disabled}
            required
            id={`species-${animal.id}`}
            name={`species-${animal.id}`}
            label={t('pages.step1.animalInputData.species')}
            value={animal.species}
            onValueChanged={(id: any, value: any) => {
              const _specie = formatterSpeciesOptions?.find(
                (item) => item.value === value,
              )

              if (_specie?.caption?.length) {
                handleEditSafeAnimal('species', _specie?.caption!)
                handleEditSafeAnimal('speciesCode', value)
              } else {
                handleEditSafeAnimal('species', '')
                handleEditSafeAnimal('speciesCode', '')
                handleEditSafeAnimal('breed', '')
                handleEditSafeAnimal('breedCode', '')
              }
            }}
            colorTheme="blue"
            options={formatterSpeciesOptions}
            version="2"
          />
          <Dropdown
            readOnly={
              isGenerateProposalDocuments ||
              currentSpecies.length === 0 ||
              disabled
            }
            required
            id={`breed-${animal.id}`}
            name={`breed-${animal.id}`}
            label={t('pages.step1.animalInputData.breed')}
            value={animal.breed}
            onValueChanged={(id: any, value: any) => {
              const _breeds = formatterBreedOptionsBySpecies(currentSpecies)
              const currentBreed = _breeds?.find(
                (breed) => breed.value === value,
              )

              if (currentBreed?.caption?.length) {
                handleEditSafeAnimal('breed', currentBreed?.caption!)
                handleEditSafeAnimal('breedCode', value)
              } else {
                handleEditSafeAnimal('breed', '')
                handleEditSafeAnimal('breedCode', '')
              }
            }}
            colorTheme="blue"
            options={formatterBreedOptionsBySpecies()}
            version="2"
          />
        </div>
        <div className="row">
          <div className="col" />
          <div className="col">
            {breeds?.find((breed) => animal.breedCode === breed.value)
              ?.isDangerousAnimal && (
              <Message
                type="warning"
                text={t('pages.step1.breedDangerousMessage')}
              />
            )}
          </div>
        </div>
      </FormContainer>
      <ActionTab>
        <Action
          id="actionTab"
          onValueChanged={onNextTab}
          colorTheme="blue"
          buttonType="secondary"
          label={t('pages.step1.nextTab')}
          disable={disabledNextTabButton}
        />
      </ActionTab>
    </>
  )
}
